import { z } from 'zod';

export const STORE_ACCESS = ['all', 'selected'] as const;
export type StoreAccess = (typeof STORE_ACCESS)[number];

const roleKey = z.string().min(1, { error: 'team:validation.role' });
const access = z.enum(STORE_ACCESS);
const storeIds = z.array(z.string());

const hasStores = (v: { access: StoreAccess; storeIds: string[] }) =>
  v.access === 'all' || v.storeIds.length > 0;
const storesIssue = { path: ['storeIds'], error: 'team:validation.storesRequired' };

export const inviteMemberSchema = z
  .object({
    email: z.email({ error: 'team:validation.email' }),
    name: z
      .string()
      .trim()
      .min(1, { error: 'team:validation.required' })
      .max(120, { error: 'team:validation.nameLength' }),
    roleKey,
    access,
    storeIds,
  })
  .refine(hasStores, storesIssue);

export type InviteMemberInput = z.input<typeof inviteMemberSchema>;
export type InviteMemberOutput = z.output<typeof inviteMemberSchema>;

/** One role for every store, or a different role in some stores (the rest keep the default). */
export const ACCESS_SCOPE = ['same', 'perStore'] as const;
export type AccessScope = (typeof ACCESS_SCOPE)[number];

const permissionList = z.array(z.string());

/** A store row of the per-store mode; an empty `roleKey` means the store uses the default role. */
const storeRoleRow = z.object({
  storeId: z.string(),
  roleKey: z.string(),
  grantedPermissions: permissionList,
  revokedPermissions: permissionList,
});

export const editMemberSchema = z
  .object({
    roleKey,
    access,
    storeIds,
    grantedPermissions: permissionList,
    revokedPermissions: permissionList,
    scope: z.enum(ACCESS_SCOPE),
    storeRoles: z.array(storeRoleRow),
  })
  .refine(hasStores, storesIssue);

export type EditMemberInput = z.input<typeof editMemberSchema>;
export type EditMemberOutput = z.output<typeof editMemberSchema>;
